import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { ContentContestDto } from '@diritto/common/content/dtos/content-contest.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { removeNull } from '@diritto/common/utils/array';
import { ellipsis } from '@diritto/common/utils/string';
import { ContestStage } from '@diritto/common/content/types/content-contest-info-types';
import { MyContentItemAction, MyContentItemActionText } from 'constants/studio';
import { useTrackingState } from 'contexts/TrackingProvider';
import { studioQueryKey } from 'queries/studio/queryKey';
import { useDeleteContentMutation } from 'queries/studio/useDeleteContentMutation';
import { useWithdrawContestContentMutation } from 'queries/studio/useWithdrawContestContentMutation';
import { MoreMenu } from 'ui-components/common/MoreMenu';
import { showAlertModal, showToast } from 'utils/modal';
import { useContestStage } from '@/state-hooks/contests/useStageValidation';

interface MyContentMoreMenuProps {
  contestId: number;
  content: ContentPublicRelayDto;
  contentContest: ContentContestDto;
  onDeleted?: () => void;
  disabled?: boolean;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.DirittoStudioContentsMoreMenu,
};

// 접수기간
// - 모든 작품 : 철회, 삭제 버튼 노출 O
// 검수기간
// - 모든 작품 : 더보기 X
// - 미 접수 작품: 더보기 > 일반작품 전환, 삭제 (철회가 의미 없음)
// 투표 기간
// - 투표 대상작 : 더보기 X
// - 미접수, 검수 미통과: 더보기 > 일반작품 전환, 삭제 (철회가 의미 없음)
// 수상작 발표 → 픽앤츄 탭 없어짐

export const ContestContentMoreMenu = ({ contestId, content, contentContest, onDeleted }: MyContentMoreMenuProps) => {
  const { mutateAsync: deleteMutate } = useDeleteContentMutation(content.id);
  const { mutateAsync: withdrawContestMutate } = useWithdrawContestContentMutation(contestId, content.id);
  const { tracker } = useTrackingState();
  const queryClient = useQueryClient();
  const stage = useContestStage(contestId);

  const disabled = useMemo(() => {
    if (stage === ContestStage.Writing) {
      return false;
    }
    if (stage && stage <= contentContest.stage) {
      return true;
    }
    return false;
  }, [stage, contentContest.stage]);

  const actions = useMemo(
    () =>
      disabled
        ? []
        : removeNull([
            MyContentItemAction.DeleteMyContent,
            stage === ContestStage.Writing
              ? MyContentItemAction.WithDrawContest
              : MyContentItemAction.TurnToNormalContent,
          ]),
    [disabled, stage],
  );

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType) => {
      tracker.track(TrackingEventType.Click, { action, clickType, trackerExtraInfo });
    },
    [tracker],
  );

  const handleDelete = useCallback(async () => {
    try {
      if (content.id) {
        await deleteMutate();
        onDeleted?.();
        tracker.track(TrackingEventType.DeleteContent, { contentId: content.id, trackerExtraInfo });
        await queryClient.invalidateQueries({ queryKey: studioQueryKey.contents });
        showToast('작품이 삭제되었어요.');
      }
    } catch (e) {
      showAlertModal('삭제에 실패했어요.');
    }
  }, [content.id, deleteMutate, onDeleted, queryClient, tracker]);

  const handleWithdrawContest = useCallback(async () => {
    try {
      if (content.id) {
        await withdrawContestMutate();
        await queryClient.invalidateQueries({ queryKey: studioQueryKey.contents });
        showToast('픽앤츄 접수가 철회되었어요.');
        window.location.reload();
      }
    } catch (e) {
      showAlertModal('철회에 실패했어요.');
    }
  }, [content.id, queryClient, withdrawContestMutate]);

  const handleAction = useCallback(
    async (action: MyContentItemAction) => {
      switch (action) {
        case MyContentItemAction.DeleteMyContent:
          handleTrack(ClickAction.DeleteContent, ClickTargetType.MoreMenu);
          tracker.track(TrackingEventType.ClickDeleteContent, { contentId: content.id, trackerExtraInfo });

          showAlertModal(
            {
              title: `<${ellipsis(content.title, 12)}> 작품을 삭제하시겠어요?`,
              description: '삭제 시, 영구적으로 삭제되며, 되돌릴 수 없어요.',
            },
            {
              cancelText: '취소',
              confirmText: '작품 삭제',
              onConfirm: handleDelete,
            },
          );
          return;
        case MyContentItemAction.WithDrawContest:
          showAlertModal(
            {
              title: `픽앤츄 접수를 철회하시겠어요?`,
              description:
                '철회할 경우 접수했던 작품은 일반 디리토 작품으로 변경되며, 픽앤츄에 참여하려면 다시 작품을 생성해야 해요.\n\n' +
                '접수 철회는 되돌릴 수 없어요.',
            },
            {
              cancelText: '취소',
              confirmText: '접수 철회',
              onConfirm: handleWithdrawContest,
            },
          );
          return;
        case MyContentItemAction.TurnToNormalContent:
          showAlertModal(
            {
              title: `픽앤츄 접수를 일반 작품으로 전환하시겠어요?`,
              description: '전환 시, 작품이 일반 디리토 작품으로 변경되며 바로 연재를 시작할 수 있어요.',
            },
            {
              cancelText: '취소',
              confirmText: '전환하기',
              onConfirm: handleWithdrawContest,
            },
          );
          return;
        default:
          console.warn('Undefined Action', action);
      }
    },
    [content.id, content.title, handleDelete, handleTrack, handleWithdrawContest, tracker],
  );

  const menu = useMemo(
    () =>
      actions.map(action => ({
        title: MyContentItemActionText[action] || '',
        onClick: () => handleAction(action),
      })),
    [actions, handleAction],
  );

  return <MoreMenu vertical menu={menu} disabled={disabled} />;
};
